/**
 * AppLovin 创意组名（Creative-set）拼装：按 dev-docs `02-API契约/85-归因与深链.md` §1.2
 * 「创意组名格式」从项目信息派生，供运营复制到 AppLovin 后台。
 *
 * 段序沿运营样例 `BOOK-…_BY-…_OS-…_LANG-…_APP-…_UTMSOURCE-APPLOVIN_TAG-…`：
 * BOOK 必须首段（服务端只认首段 BOOK）；TAG 可缺省；其余缺任一段即不给出名称，
 * 避免运营复制到一个残缺、会被归因侧当成普通创意名的串。
 */
import type { AppPlatform } from '@novel-admin/shared';

/** 唯一走创意组名分支的渠道（85 §1.2 `media_source = applovin_int`）*/
export const APPLOVIN_MEDIA_SOURCE = 'applovin_int';

/** 85 §1.2 全串上限（AppsFlyer `af_ad` 取值上限）*/
const CREATIVE_SET_NAME_MAX_LENGTH = 100;
/** 85 §1.2 允许字符集 */
const CREATIVE_SET_NAME_PATTERN = /^[A-Za-z0-9._-]+$/;

/** OS 段取值：契约登记 IOS / AND，WEB 产品沿用平台名 */
const OS_SEGMENT_BY_PLATFORM: Record<AppPlatform, string> = {
  IOS: 'IOS',
  ANDROID: 'AND',
  WEB: 'WEB',
};

export type CreativeSetNameInput = {
  bookCode: string;
  /** 当前登录账号 CODE → BY 段 */
  accountCode: string | null | undefined;
  platform: AppPlatform;
  locale: string;
  /** App 包体代号 `apps.app_code` → APP 段 */
  appCode: string | null | undefined;
  tag: string | null | undefined;
};

export type CreativeSetNameResult = { ok: true; name: string } | { ok: false; reason: string };

export function buildApplovinCreativeSetName(input: CreativeSetNameInput): CreativeSetNameResult {
  const missing = [
    !input.bookCode.trim() && '书级编码',
    !input.accountCode?.trim() && '当前账号 CODE',
    !input.appCode?.trim() && 'App 包体代号',
  ].filter((label): label is string => Boolean(label));
  if (missing.length) return { ok: false, reason: `缺少${missing.join('、')}` };
  const segments: [string, string][] = [
    ['BOOK', input.bookCode.trim()],
    ['BY', (input.accountCode ?? '').trim().toUpperCase()],
    ['OS', OS_SEGMENT_BY_PLATFORM[input.platform]],
    ['LANG', input.locale.trim()],
    ['APP', (input.appCode ?? '').trim()],
    ['UTMSOURCE', 'APPLOVIN'],
  ];
  const tag = input.tag?.trim();
  if (tag) segments.push(['TAG', tag]);
  // value 内出现 `_` 会把段切坏，统一在这里兜住而不是信任各来源字段
  const brokenSegment = segments.find(([, value]) => value.includes('_'));
  if (brokenSegment) return { ok: false, reason: `${brokenSegment[0]} 段不能包含下划线` };
  const name = segments.map(([key, value]) => `${key}-${value}`).join('_');
  if (!CREATIVE_SET_NAME_PATTERN.test(name))
    return { ok: false, reason: '只能包含英文字母、数字、点、下划线和短横线' };
  if (name.length > CREATIVE_SET_NAME_MAX_LENGTH)
    return { ok: false, reason: `超过 ${CREATIVE_SET_NAME_MAX_LENGTH} 字符，请缩短 TAG` };
  return { ok: true, name };
}
